import React from 'react';
import Select from 'react-select'; // Import react-select

function SelectInput({
    label,
    id,
    name,
    value,
    onChange,
    required = false,
    multiple = false,
    options, // Array of { value: string, label: string }
    defaultOptionText = "Selecciona una opción..."
}) {
    // react-select's onChange provides the selected option object (or array of objects for multi)
    // We need to adapt this to fit the existing handleChange structure in RegisterScreen
    const handleSelectChange = (selectedOption) => {
        let valueToSet;
        if (multiple) {
            // selectedOption will be an array of option objects or null
            valueToSet = selectedOption ? selectedOption.map(option => option.value) : [];
        } else {
            // selectedOption will be a single option object or null
            valueToSet = selectedOption ? selectedOption.value : '';
        }
        // Call the original onChange with a synthetic event-like structure
        onChange({ target: { name: name, value: valueToSet, type: multiple ? 'select-multiple' : 'select-one' } });
    };

    // react-select expects the `value` prop to be the full option object(s),
    // not just the value string. We need to find the corresponding option object(s).
    let currentValue;
    if (multiple) {
        currentValue = Array.isArray(value) ? options.filter(option => value.includes(option.value)) : [];
    } else {
        currentValue = options.find(option => option.value === value) || null;
    }

    // Custom styles to make react-select look more like your original Select2 styling
    const customStyles = {
        control: (provided, state) => ({
            ...provided,
            minHeight: '44px', // Matches .form-control height from style.css
            height: multiple ? 'auto' : '44px', 
            // Focus style from .select2-container--default.select2-container--focus .select2-selection--multiple
            borderColor: state.isFocused ? '#000000' : '#ced4da', // var(--color-negro) for focus, default otherwise
            borderWidth: state.isFocused ? '2px' : '1px',
            boxShadow: 'none', // Remove default react-select focus shadow
            borderRadius: '4px', // Matches .select2-selection--multiple and .form-control
            '&:hover': {
                borderColor: state.isFocused ? '#000000' : '#adb5bd', 
            },
        }),
        valueContainer: (provided, state) => ({
            ...provided,
            padding: multiple ? '2px 8px' : `0 8px`, // Adjusted padding for multi vs single
            minHeight: '42px', // Ensure inner container respects min height
            height: multiple ? 'auto' : '42px',
            display: 'flex', // Keep flex for alignment
            alignItems: 'center', // Vertically center content
        }),
        input: (provided) => ({ // The actual text input field within the control
            ...provided,
            margin: '0px',
            paddingTop: '0px',
            paddingBottom: '0px',
            color: '#1B1B1B', 
        }),
        placeholder: (provided) => ({
            ...provided,
            color: '#6c757d', // Bootstrap's placeholder color
            marginLeft: '2px',
            marginRight: '2px',
        }),
        singleValue: (provided) => ({ // Styles for the selected text in single select
            ...provided,
            color: '#1B1B1B', 
            marginLeft: '2px', // Align with placeholder
            marginRight: '2px',
            lineHeight: '38px', // Approximate vertical center for 44px height
        }),
        multiValue: (styles) => ({ // The "pill" for a selected item in multi-select
            ...styles,
            backgroundColor: '#000000', // var(--color-negro)
            borderRadius: '4px', // from .select2-selection__choice
            margin: '3px 3px', // Consistent with Select2 .select2-selection__choice margin
            padding: '0px', // Remove default padding, control via label/remove
            // paddingLeft: '22px', // This was for an icon in Select2, if you need an icon, it's a different approach
        }),
        multiValueLabel: (styles) => ({ // Text inside the pill
            ...styles,
            color: '#FFFFFF', // var(--color-blanco)
            padding: '3px', // Minimal padding
            paddingLeft: '6px', // from .select2-selection__choice__display
            paddingRight: '6px',
            fontSize: '17px', // from .select2-selection__choice
        }),
        multiValueRemove: (styles) => ({ // The 'x' icon to remove a pill
            ...styles,
            color: '#FFFFFF', // var(--color-blanco)
            borderRadius: '0 4px 4px 0', // Match pill's right border radius
            paddingLeft: '4px', // Give some space for the 'x'
            paddingRight: '4px',
            ':hover': {
                backgroundColor: '#000000', // Keep black background on hover
                color: '#F2C400', // var(--color-acento-amarillo) for remove icon hover
            },
        }),
        indicatorsContainer: (provided) => ({ // Container for dropdown arrow, clear button
            ...provided,
            height: '42px', // Match control inner height
        }),
        dropdownIndicator: (provided, state) => ({ // The dropdown arrow
            ...provided,
            padding: 8,
            color: state.isFocused ? '#000000' : '#5a646d', // Black when focused, otherwise secondary text color
            transition: 'transform 0.2s ease',
            transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : null,
            ':hover': {
                color: '#000000', 
            }
        }),
        clearIndicator: (provided) => ({
            ...provided,
            padding: 8,
            color: '#5a646d',
            ':hover': {
                color: '#000000',
            }
        }),
        option: (styles, { isSelected, isFocused, isDisabled }) => { // An item in the dropdown list
            return {
                ...styles,
                backgroundColor: isDisabled
                    ? undefined
                    : isSelected
                        ? '#f0f0f0' // style.css .select2-results__option[aria-selected=true]
                        : isFocused
                            ? '#000000' // style.css .select2-results__option--highlighted
                            : undefined,
                color: isDisabled
                    ? '#ccc'
                    : isSelected
                        ? '#1B1B1B' // Text color for selected item
                        : isFocused
                            ? '#FFFFFF' // Text color for focused/highlighted item
                            : '#1B1B1B',
                cursor: isDisabled ? 'not-allowed' : 'default',
                padding: '10px 12px', // Decent padding for options

                ':active': { // For when an option is clicked
                    ...styles[':active'],
                    backgroundColor: !isDisabled ? '#000000' : undefined, // Keep black on active click
                    color: !isDisabled ? '#FFFFFF' : undefined,
                },
            };
        },
        menu: (provided) => ({ // The dropdown menu itself
            ...provided,
            marginTop: '2px', // Small gap between control and menu
            borderRadius: '4px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)', 
        }),
        menuList: (provided) => ({ // Inner list of options
            ...provided,
            paddingTop: '4px',
            paddingBottom: '4px',
        }),
        noOptionsMessage: (provided) => ({
            ...provided,
            padding: '10px 12px',
            textAlign: 'center',
            color: '#5a646d',
        }),
    };

    return (
        <>
            <label htmlFor={id} className="form-label">{label}</label>
            <Select
                id={id}
                name={name}
                options={options}
                value={currentValue}
                onChange={handleSelectChange}
                isMulti={multiple}
                placeholder={defaultOptionText}
                isClearable
                // The 'required' prop is not directly supported by react-select for native form validation.
                // You'd handle 'required' logic in your form submission if needed (as you are doing in RegisterScreen).
                styles={customStyles} // Apply custom styles
                theme={(theme) => ({ // Basic theme adjustments
                    ...theme,
                    borderRadius: 4, // Matches .form-control and .select2-selection--multiple
                    colors: {
                        ...theme.colors, // Default react-select theme colors
                        primary: '#000000', // Main focus color (used by react-select for focused border if not overridden by 'control' styles)
                        primary75: '#333333',
                        primary50: '#666666',
                        primary25: '#f0f0f0', // Light hover for options if not overridden
                    },
                })}
            // For localization of "No options" message, etc.
            // noOptionsMessage={() => "No hay opciones"}
            />
            {/* 
                If native 'required' behavior is essential for some reason, you might need a hidden input.
                However, handling validation in handleSubmit (as in RegisterScreen.jsx) is the more common React way.
                multiple && required && Array.isArray(value) && value.length === 0 ? (
                    <input type="text" name={`${name}_hidden_required`} value="" required style={{ display: 'none', position: 'absolute', zIndex: -1, opacity: 0, width: 0, height: 0 }} />
                ) : !multiple && required && !value ? (
                    <input type="text" name={`${name}_hidden_required`} value="" required style={{ display: 'none', position: 'absolute', zIndex: -1, opacity: 0, width: 0, height: 0 }} />
                ) : null
            */}
        </>
    );
}

export default SelectInput;
